import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  DashboardControls,
  useAutoRefresh,
  useDashboardWindow,
} from '@/components/DashboardControls';
import {
  Badge,
  Card,
  Delta,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  StatCard,
} from '@/components/ui';
import { formatDateTime, formatMoney, formatNumber } from '@/lib/format';
import {
  HEALTH_LABEL,
  HEALTH_TONE,
  needsAttention,
  providerHealth,
} from '@/lib/providerHealth';

/**
 * The page somebody opens first.
 *
 * It used to be an inventory: how many clients, how many bots, how many
 * assignments. All true, all unchanged since last Tuesday, and none of it the
 * question anybody has at nine in the morning — which is "is this working
 * right now, and is it costing what it should".
 *
 * So the inventory moved to one quiet strip at the bottom and the top of the
 * page is today: calls, transfers, spend, each against the same point
 * yesterday, because a number with nothing to compare it to is not
 * information. Underneath, the three things that go wrong — open alerts,
 * errors by source, and campaigns that are dialling.
 */

interface Overview {
  counts: {
    clients: number;
    active_clients: number;
    bots: number;
    bot_groups: number;
    vicidial_users: number;
    assignments: number;
    calls_in_window: number;
    live_calls: number;
  };
  providers: Array<{
    kind: string;
    key: string;
    name: string;
    enabled: boolean;
    is_default: boolean;
    health_state: string;
  }>;
  window?: { key: string; label: string };
  today: {
    calls: number;
    transfers: number;
    qualified: number;
    answered: number;
    machines: number;
    clients_dialling: number;
    spend: number;
    billed: number;
    avg_seconds: number;
  };
  yesterday: { calls: number; transfers: number };
  errors_1h: Array<{ source: string; count: number }>;
  busiest: Array<{
    id: string;
    name: string;
    calls: number;
    transfers: number;
    spend: string | number;
  }>;
  campaigns: Array<{
    campaign: string;
    client_name: string;
    calls: number;
    transfers: number;
    last_call: string;
  }>;
  alerts: Array<{
    id: string;
    severity: string;
    title: string;
    fired_at: string;
    acknowledged_at: string | null;
    client_name: string | null;
  }>;
}

const SEVERITY: Record<string, 'red' | 'amber' | 'blue'> = {
  critical: 'red',
  warning: 'amber',
  info: 'blue',
};

function duration(seconds: number): string {
  if (!seconds) return '—';
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

function rate(part: number, whole: number): string {
  if (!whole) return '—';
  return `${((part / whole) * 100).toFixed(1)}%`;
}

export default function AdminOverview() {
  const [windowKey, setWindowKey] = useDashboardWindow();
  const { on: autoRefresh, setOn: setAutoRefresh, interval: refetchInterval } =
    useAutoRefresh();

  const overview = useQuery({
    queryKey: ['admin-overview', windowKey],
    queryFn: () => api.get<Overview>('/admin/overview', { window: windowKey }),
    refetchInterval,
    // Keeps the numbers on screen while the next poll runs. Without it a
    // five-second refresh blanks the page to a spinner twelve times a minute.
    placeholderData: (previous) => previous,
  });

  if (overview.isPending) return <Loading />;
  if (overview.error)
    return <ErrorState error={overview.error} retry={() => overview.refetch()} />;

  const { counts, providers, today, yesterday, errors_1h, busiest, campaigns, alerts } =
    overview.data;

  const unhealthy = providers.filter(
    // Was `!== 'OK'`, and the backend writes 'HEALTHY' -- so every working
    // provider was listed as needing attention on the first screen an
    // operator opens.
    needsAttention,
  );

  /** Cost per transfer: the number the whole business turns on. */
  const perTransfer = today.transfers ? today.spend / today.transfers : null;

  /** What the server called the period, so every heading names the same one. */
  const periodLabel = overview.data?.window?.label ?? 'Today';

  return (
    <div className="w-full">
      <PageHeader
        title="Overview"
        description={`${periodLabel}, against the same length of time before it.`}
        actions={
          <DashboardControls
            window={windowKey}
            onWindow={setWindowKey}
            autoRefresh={autoRefresh}
            onAutoRefresh={setAutoRefresh}
            updatedAt={overview.dataUpdatedAt}
            extra={
              <Link
                to="/live-calls"
                className="whitespace-nowrap text-sm text-brand-600 hover:underline dark:text-brand-400"
              >
                {counts.live_calls} live now →
              </Link>
            }
          />
        }
      />

      {/* Anything actually wrong goes above everything else. A dashboard that
          puts an inventory above an open critical alert is decoration. */}
      {!!alerts.length && (
        <Card
          className="mb-4 border-amber-300 dark:border-amber-800/60"
          title={`${alerts.length} open alert${alerts.length === 1 ? '' : 's'}`}
          actions={
            <Link
              to="/alerting"
              className="text-xs text-brand-600 hover:underline dark:text-brand-400"
            >
              Alerting →
            </Link>
          }
          padded={false}
        >
          <ul className="divide-y divide-line">
            {alerts.map((alert) => (
              <li
                key={alert.id}
                className="flex items-center gap-2 px-4 py-2 text-sm"
              >
                <Badge tone={SEVERITY[alert.severity] ?? 'gray'}>
                  {alert.severity}
                </Badge>
                <span className="min-w-0 flex-1 truncate text-ink-800 dark:text-ink-100">
                  {alert.title}
                </span>
                {!alert.acknowledged_at && (
                  <Badge tone="gray">new</Badge>
                )}
                <span className="shrink-0 text-2xs text-ink-400">
                  {formatDateTime(alert.fired_at)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard
          label="Calls"
          value={formatNumber(today.calls)}
          sub={<Delta now={today.calls} before={yesterday.calls} />}
          dense
        />
        <StatCard
          label="Transfers"
          value={formatNumber(today.transfers)}
          sub={<Delta now={today.transfers} before={yesterday.transfers} />}
          tone={today.transfers ? 'good' : 'default'}
          dense
        />
        <StatCard
          label="Transfer rate"
          value={rate(today.transfers, today.calls)}
          sub={`${formatNumber(today.answered)} answered`}
          dense
        />
        <StatCard
          label="Cost / transfer"
          value={perTransfer === null ? '—' : formatMoney(perTransfer)}
          sub={`${formatMoney(today.spend)} spent`}
          tone={perTransfer !== null && perTransfer > 5 ? 'warn' : 'default'}
          dense
        />
        <StatCard
          label="Machines"
          value={formatNumber(today.machines)}
          sub={rate(today.machines, today.calls) + ' of calls'}
          dense
        />
        <StatCard
          label="Average length"
          value={duration(today.avg_seconds)}
          sub={`${today.clients_dialling} client${today.clients_dialling === 1 ? '' : 's'} dialling`}
          dense
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card
          title="Errors in the last hour"
          description="A degrading vendor is invisible in any one call and obvious in the aggregate."
          padded={false}
          actions={
            <Link
              to="/system-logs?level=ERROR&hours=1"
              className="text-xs text-brand-600 hover:underline dark:text-brand-400"
            >
              Open logs →
            </Link>
          }
        >
          {!errors_1h.length ? (
            <EmptyState title="Nothing errored" description="A quiet hour." />
          ) : (
            <ul className="divide-y divide-line">
              {errors_1h.map((row) => (
                <li
                  key={row.source}
                  className="flex items-center justify-between px-4 py-2 text-sm"
                >
                  <Link
                    to={`/system-logs?level=ERROR&hours=1&source=${encodeURIComponent(row.source)}`}
                    className="truncate font-mono text-xs text-ink-700 hover:text-brand-600 dark:text-ink-200 dark:hover:text-brand-400"
                  >
                    {row.source}
                  </Link>
                  <span className="tabular-nums text-ink-900 dark:text-ink-50">
                    {formatNumber(row.count)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title="Campaigns dialling"
          description="The last two hours."
          padded={false}
        >
          {!campaigns.length ? (
            <EmptyState title="Nothing is dialling" description="No calls in two hours." />
          ) : (
            <ul className="divide-y divide-line">
              {campaigns.map((row) => (
                <li key={`${row.client_name}:${row.campaign}`} className="px-4 py-2">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-ink-800 dark:text-ink-100">
                      {row.campaign}
                    </span>
                    <span className="shrink-0 tabular-nums text-xs text-ink-500">
                      {formatNumber(row.calls)} calls ·{' '}
                      <span
                        className={
                          row.transfers
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-amber-600 dark:text-amber-400'
                        }
                      >
                        {formatNumber(row.transfers)} transferred
                      </span>
                    </span>
                  </div>
                  <p className="text-2xs text-ink-400">{row.client_name}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={`Busiest clients · ${periodLabel}`} padded={false}>
          {!busiest.length ? (
            <EmptyState
              title={`Nothing in ${periodLabel.toLowerCase()}`}
              description="No calls yet."
            />
          ) : (
            <ul className="divide-y divide-line">
              {busiest.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between px-4 py-2 text-sm"
                >
                  <Link
                    to={`/clients/${row.id}`}
                    className="truncate text-brand-600 hover:underline dark:text-brand-400"
                  >
                    {row.name}
                  </Link>
                  <span className="shrink-0 tabular-nums text-xs text-ink-500">
                    {formatNumber(row.calls)} calls · {formatNumber(row.transfers)} xfer ·{' '}
                    {formatMoney(Number(row.spend))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          title="Providers"
          description={
            unhealthy.length
              ? `${unhealthy.length} not healthy`
              : 'All enabled providers are answering.'
          }
          padded={false}
          actions={
            <Link
              to="/provider-status"
              className="text-xs text-brand-600 hover:underline dark:text-brand-400"
            >
              Detail →
            </Link>
          }
        >
          <ul className="divide-y divide-line">
            {providers
              .filter((p) => p.enabled)
              .map((provider) => (
                <li
                  key={`${provider.kind}:${provider.key}`}
                  className="flex items-center justify-between px-4 py-2 text-sm"
                >
                  <span className="truncate text-ink-800 dark:text-ink-100">
                    {provider.name}
                    <span className="ml-1.5 text-2xs uppercase text-ink-400">
                      {provider.kind}
                    </span>
                  </span>
                  <Badge tone={HEALTH_TONE[providerHealth(provider.health_state)]}>
                    {HEALTH_LABEL[providerHealth(provider.health_state)]}
                  </Badge>
                </li>
              ))}
          </ul>
        </Card>
      </div>

      {/* The inventory. True, useful occasionally, and unchanged since last
          Tuesday -- so it sits at the bottom in one quiet strip rather than
          taking the top of the page. */}
      <div className="mt-4 rounded-xl border border-line bg-surface px-4 py-3">
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-ink-500">
          {[
            ['Clients', `${counts.active_clients} of ${counts.clients} active`],
            ['Bots', counts.bots],
            ['Bot groups', counts.bot_groups],
            ['Dialer users', counts.vicidial_users],
            ['Assignments', counts.assignments],
            // Named after the period on screen. It said "Calls, 24h" whatever
            // was picked, so choosing "last month" relabelled nothing and the
            // row read as a day's dialling.
            [
              `Calls, ${periodLabel.toLowerCase()}`,
              formatNumber(counts.calls_in_window),
            ],
          ].map(([label, value]) => (
            <span key={String(label)}>
              {label}{' '}
              <strong className="tabular-nums text-ink-800 dark:text-ink-100">
                {value}
              </strong>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
